import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Network, Sparkles, ShoppingBag, Rocket, Truck, Store, Building2, ArrowRight, Users, Heart, Eye, Camera, Megaphone,
} from "lucide-react";
import { trackEvent } from "@/lib/retention";
import OrbitalEcosystem from "@/components/public/OrbitalEcosystem";
import PageMeta from "@/components/PageMeta";
import EcosystemFlowSection from "@/components/public/EcosystemFlowSection";
import ComplianceDisclaimer from "@/components/public/ComplianceDisclaimer";
import BrandMark from "@/components/lifted/BrandMark";
import SignupOptionsSection from "@/components/public/SignupOptionsSection";

/**
 * Phase 11.5 / 11.6 — /ecosystem master explanation page.
 *
 * 11.6 correction:
 *   - 5 roles only: Customer · Affiliate · Driver · Partner Store · Franchise
 *   - Content creators / influencers / promoters / photographers / videographers
 *     / designers / community builders are FUNCTIONS of the Affiliate program,
 *     NOT separate roles.
 *   - "Community" is an outcome of the 5 roles, not a role itself.
 *   - "Future Franchise" → "Franchise".
 *
 * Sections: introduction · connections · role breakdown · affiliate
 * heartbeat · partner-store value · growth engine · growth loop ·
 * choose your path.
 */
const ROLES = [
  { key: "customer",  icon: ShoppingBag, label: "Customer",      to: "/shop",
    blurb: "Purchases products and supports the brand. The reason every other role exists." },
  { key: "affiliate", icon: Rocket,      label: "Affiliate",     to: "/affiliate-zone",
    blurb: "The marketing engine. Builds community and content. Helps introduce customers, drivers, partner stores, and future franchisees. Commission opportunities follow participation." },
  { key: "driver",    icon: Truck,       label: "Driver",        to: "/apply/driver",
    blurb: "Completes Lifted Supplies deliveries. Must pass approval and background screening." },
  { key: "partner",   icon: Store,       label: "Partner Store", to: "/apply/partner",
    blurb: "Provides inventory and local fulfillment for the network. Plugs into affiliate-driven exposure. Requires approval." },
  { key: "franchise", icon: Building2,   label: "Franchise",     to: "/apply/franchise",
    blurb: "Expands the ecosystem into a new market. Becomes part of the larger Lifted network — not separate from it. Requires approval." },
];

const AFFILIATE_FUNCTIONS = []; // removed in 12.3 — chips were visually implying separate roles. Functions are now a single sentence inside the heartbeat copy.

const PARTNER_BENEFITS = [
  { icon: Eye,       title: "Exposure",          blurb: "Affiliates put partner stores in front of new audiences." },
  { icon: Sparkles,  title: "Awareness",         blurb: "Real content, real reach — beyond traditional ads." },
  { icon: Users,     title: "Social proof",      blurb: "Community-backed visibility, not paid placements." },
  { icon: Camera,    title: "Content",           blurb: "Affiliates produce posts, videos, and stories highlighting partner offerings." },
  { icon: Megaphone, title: "Word-of-mouth",     blurb: "Distributed marketing across many networks at once." },
  { icon: Network,   title: "Network visibility",blurb: "Plugged into the broader Lifted ecosystem from day one." },
];

const GROWTH_LOOP = [
  "Awareness", "Customers", "Sales", "Deliveries", "Drivers", "Stores", "Franchises", "More awareness",
];

export default function Ecosystem() {
  useEffect(() => { trackEvent("ecosystem_view"); }, []);

  return (
    <div data-testid="ecosystem-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <PageMeta title="Ecosystem" path="/ecosystem" ogImage="/og-ecosystem.png"
                description="One brand. Five roles. Affiliates · Drivers · Partners · Franchisees · Customers — all connected in the Lifted Ecosystem." />
      {/* SECTION 1 — Introduction */}
      <header data-testid="ecosystem-intro" className="mb-16">
        <BrandMark tag="logo.ecosystem.primary" size="lg" className="mb-6" testId="ecosystem-zone-logo" />
        <div className="eyebrow inline-flex items-center gap-2"><Network className="h-3 w-3" /> The Lifted Ecosystem</div>
        <h1 className="font-anton text-5xl sm:text-6xl uppercase mt-3 leading-[0.95] max-w-4xl">
          What is the<br /><span className="text-lifted-green">Lifted ecosystem?</span>
        </h1>
        <p className="text-lifted-dim text-base sm:text-lg mt-5 max-w-3xl">
          LiftedAtTheMoment is more than a clothing brand. It's a connected network of five roles — customers,
          affiliates, drivers, partner stores, and franchisees — all sharing the same brand, the same standards,
          and the same lifestyle.
        </p>
      </header>

      {/* SECTION 2 — How Everything Connects */}
      <section data-testid="ecosystem-connections" className="mb-16 glass-panel p-6 sm:p-10">
        <div className="eyebrow flex items-center gap-2"><Users className="h-3 w-3" /> How it connects</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">Every role flows into the next.</h2>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {ROLES.map((r, i) => (
            <div key={r.key} data-testid={`ecosystem-connect-${i}`} className="glass-panel p-4 text-center">
              <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-mute">STEP {i + 1}</div>
              <div className="font-anton uppercase text-base text-lifted-green mt-1">{r.label}</div>
            </div>
          ))}
        </div>
        <p className="text-[0.7rem] text-lifted-mute mt-6 leading-relaxed">
          Community is the outcome of all five roles working together — it is not a separate position.
        </p>
      </section>

      {/* SECTION 3 — Role Breakdown */}
      <section data-testid="ecosystem-roles" className="mb-16">
        <div className="eyebrow flex items-center gap-2"><Sparkles className="h-3 w-3" /> Role breakdown</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">Five roles. One brand.</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          {ROLES.map((r) => {
            const Icon = r.icon;
            return (
              <Link
                key={r.key}
                id={`role-${r.key}`}
                to={r.to}
                data-testid={`ecosystem-role-${r.key}`}
                onClick={() => trackEvent("ecosystem_role_click", { role: r.key, surface: "ecosystem_page" })}
                className="glass-panel p-5 hover:-translate-y-1 transition-transform"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-lifted-green/15 border border-lifted-green/30 grid place-items-center text-lifted-green">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-anton uppercase text-lg">{r.label}</div>
                    <div className="text-[0.72rem] text-lifted-dim mt-1 leading-relaxed">{r.blurb}</div>
                    {r.key === "affiliate" ? (
                      <div
                        data-testid="affiliate-role-function-note"
                        className="text-[0.68rem] text-lifted-mute mt-2 leading-snug italic"
                      >
                        Affiliates may create content, promote products, refer customers, and introduce approved candidates into the ecosystem.
                      </div>
                    ) : null}
                  </div>
                  <ArrowRight className="h-4 w-4 text-lifted-mute" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* SECTION 4 — Affiliate heartbeat (community-first framing) */}
      <section data-testid="ecosystem-heartbeat" className="mb-16 glass-panel p-6 sm:p-10">
        <div className="eyebrow flex items-center gap-2"><Heart className="h-3 w-3" /> The heartbeat</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">
          Affiliates are about<br /><span className="text-lifted-green">community, content, and growth.</span>
        </h2>
        <p className="text-lifted-dim text-sm sm:text-base mt-4 max-w-3xl">
          The Affiliate Program is Lifted's marketing engine. Affiliates build community, create content, and expand
          reach across every platform. Affiliates may create content, promote products, refer customers, and introduce
          approved candidates into the ecosystem — these activities are <strong className="text-lifted-text">functions of the affiliate role, not separate positions.</strong>
          Commission opportunities exist, but they follow participation — they do not lead it.
        </p>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="glass-panel p-4">
            <div className="font-anton uppercase text-base text-lifted-green">Affiliates help introduce</div>
            <ul className="mt-2 text-[0.78rem] text-lifted-text space-y-1">
              <li>› customers</li>
              <li>› drivers</li>
              <li>› partner stores</li>
              <li>› potential franchisees</li>
              <li>› community members</li>
            </ul>
          </div>
          <div className="glass-panel p-4">
            <div className="font-anton uppercase text-base text-lifted-green">Approval still required</div>
            <ul className="mt-2 text-[0.78rem] text-lifted-text space-y-1">
              <li>› Introducing a driver does <strong>not</strong> guarantee approval. Drivers require screening.</li>
              <li>› Introducing a store does <strong>not</strong> guarantee approval.</li>
              <li>› Introducing a franchise candidate does <strong>not</strong> guarantee approval.</li>
              <li>› No earnings guarantees. No business guarantees.</li>
            </ul>
          </div>
        </div>
        <ComplianceDisclaimer variant="inline" testIdSuffix="heartbeat" />
      </section>

      {/* SECTION 5 — Why Partner Stores Benefit */}
      <section data-testid="ecosystem-partner-value" className="mb-16">
        <div className="eyebrow flex items-center gap-2"><Store className="h-3 w-3" /> Why partner stores benefit</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">
          Partner stores plug into a<br /><span className="text-lifted-green">distributed marketing network.</span>
        </h2>
        <p className="text-lifted-dim text-sm mt-3 max-w-3xl">
          Partner stores are <strong className="text-lifted-text">not buying advertising.</strong> They plug into the affiliate marketing network
          and benefit from community-driven exposure: content, social proof, word-of-mouth awareness, and the
          collective reach of every affiliate in the ecosystem.
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PARTNER_BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} data-testid={`partner-benefit-${b.title.toLowerCase().replace(/[^a-z0-9]/g,'-')}`} className="glass-panel p-5">
                <Icon className="h-5 w-5 text-lifted-green" />
                <div className="font-anton uppercase text-base mt-3">{b.title}</div>
                <div className="text-[0.72rem] text-lifted-dim mt-1.5 leading-relaxed">{b.blurb}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[0.65rem] text-lifted-mute mt-4">
          Factual benefits only. No earnings claims. No guarantees. Outcomes depend on store fit, local demand,
          and affiliate activity.
        </p>
      </section>

      {/* SECTION 6 — Orbital ecosystem visualization (replaces generic diagram) */}
      <OrbitalEcosystem id="ecosystem-orbital" />

      {/* SECTION 6b — How it grows (6-step funnel) */}
      <EcosystemFlowSection id="ecosystem-flow" showCta={false} />

      {/* SECTION 7 — Growth Loop */}
      <section data-testid="ecosystem-growth-loop" className="my-16 glass-panel p-6 sm:p-10">
        <div className="eyebrow flex items-center gap-2"><Sparkles className="h-3 w-3" /> The growth loop</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">Awareness fuels the loop.</h2>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {GROWTH_LOOP.map((step, i) => (
            <React.Fragment key={step}>
              <div
                data-testid={`growth-loop-${i}`}
                className="chip chip--ghost font-anton uppercase text-sm"
              >
                {step}
              </div>
              {i < GROWTH_LOOP.length - 1 ? <ArrowRight className="h-3 w-3 text-lifted-green" /> : null}
            </React.Fragment>
          ))}
        </div>
        <p className="text-[0.72rem] text-lifted-mute mt-6 max-w-3xl leading-relaxed">
          Driver, partner, and franchise applications go through Lifted approval and screening. Introducing a
          candidate does not guarantee acceptance.
        </p>
      </section>

      {/* SECTION 8 — Choose Your Path (capture CTAs) */}
      <section data-testid="ecosystem-cta-row" className="mb-10">
        <div className="eyebrow flex items-center gap-2"><Network className="h-3 w-3" /> Take the next step</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">Find your role in the ecosystem.</h2>
        <p className="text-lifted-dim text-sm mt-3 max-w-3xl">
          Customer accounts are open. Affiliate, partner, and franchisee access requires a valid access code or admin approval. Driver applications go through Lifted screening.
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Link to="/shop" className="glass-panel p-5 hover:-translate-y-1 transition-transform" data-testid="ecosystem-cta-shop">
            <ShoppingBag className="h-5 w-5 text-lifted-green" />
            <div className="font-anton uppercase text-sm mt-3">Shop Lifted</div>
            <div className="text-[0.7rem] text-lifted-dim mt-1">Lifted Gear &amp; Accessories.</div>
          </Link>
          <Link to="/affiliate-zone" className="glass-panel p-5 hover:-translate-y-1 transition-transform" data-testid="ecosystem-cta-affiliate">
            <Rocket className="h-5 w-5 text-lifted-green" />
            <div className="font-anton uppercase text-sm mt-3">Become an Affiliate</div>
            <div className="text-[0.7rem] text-lifted-dim mt-1">Access code or Lift-Off Tier required.</div>
          </Link>
          <Link to="/apply/driver" className="glass-panel p-5 hover:-translate-y-1 transition-transform" data-testid="ecosystem-cta-driver">
            <Truck className="h-5 w-5 text-lifted-green" />
            <div className="font-anton uppercase text-sm mt-3">Apply to Drive</div>
            <div className="text-[0.7rem] text-lifted-dim mt-1">Screening + approval required.</div>
          </Link>
          <Link to="/apply/partner" className="glass-panel p-5 hover:-translate-y-1 transition-transform" data-testid="ecosystem-cta-partner">
            <Store className="h-5 w-5 text-lifted-green" />
            <div className="font-anton uppercase text-sm mt-3">Partner with Lifted</div>
            <div className="text-[0.7rem] text-lifted-dim mt-1">Access code or admin approval.</div>
          </Link>
          <Link to="/apply/franchise" className="glass-panel p-5 hover:-translate-y-1 transition-transform" data-testid="ecosystem-cta-franchise">
            <Building2 className="h-5 w-5 text-lifted-green" />
            <div className="font-anton uppercase text-sm mt-3">Explore Franchise</div>
            <div className="text-[0.7rem] text-lifted-dim mt-1">Long-term territory builders.</div>
          </Link>
        </div>
      </section>

      {/* SECTION 9 — Where do you fit (compact role tiles) */}
      <section data-testid="ecosystem-choose-path" className="mb-12">
        <div className="eyebrow flex items-center gap-2"><Network className="h-3 w-3" /> Choose your path</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">Where do you fit?</h2>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
          {ROLES.map((r) => {
            const Icon = r.icon;
            return (
              <Link
                key={r.key}
                to={r.to}
                data-testid={`ecosystem-path-${r.key}`}
                onClick={() => trackEvent("role_selection_click", { role: r.key, surface: "choose_path" })}
                className="glass-panel p-5 hover:-translate-y-1 transition-transform flex flex-col items-center gap-2"
              >
                <Icon className="h-5 w-5 text-lifted-green" />
                <div className="font-anton uppercase text-sm">{r.label}</div>
              </Link>
            );
          })}
        </div>
        <ComplianceDisclaimer variant="inline" testIdSuffix="ecosystem" />
      </section>

      {/* Phase 22 — Public signup entry points */}
      <SignupOptionsSection
        testid="ecosystem-signup-options"
        title="Ready to plug in?"
        subtitle="Pick the role that fits — you can always add another later, all under one Lifted account."
      />
    </div>
  );
}
